import type { LeaveBalanceDto } from '@workforce/shared';
import { fmtDate } from '@/lib/format';
import { t } from '@/lib/i18n';
import { ruleLines } from '@/lib/leave';

/** Entitled / used / pending / remaining for each leave type. */
export function BalanceCards({ balances, onSelect, selectedId }: { balances: LeaveBalanceDto[]; onSelect?: (b: LeaveBalanceDto) => void; selectedId?: string }) {
  const shown = balances.filter((b) => b.deductsBalance && !b.rules?.drawsFromTypeName && (b.entitled > 0 || b.used > 0 || b.pending > 0 || b.adjusted !== 0 || b.carriedOver > 0));
  // Types without their own balance (limits only): e.g. emergency leave, sick leave with a report.
  const limits = balances.filter((b) => b.rules && (b.rules.drawsFromTypeName || (!b.deductsBalance && (b.rules.maxDaysPerYear || b.rules.maxTimesPerCareer || b.rules.requiresDocument))));
  if (!shown.length) return <p className="text-sm text-slate-500">{t('No leave allowances assigned yet. An administrator sets them through the leave policy.')}</p>;
  return (
    <div className="space-y-4">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {shown.map((b) => {
        const total = b.accrued + b.carriedOver + b.adjusted;
        const usedPct = total > 0 ? Math.min(100, (b.used / total) * 100) : 0;
        const pendPct = total > 0 ? Math.min(100 - usedPct, (b.pending / total) * 100) : 0;
        return (
          <button
            key={b.leaveTypeId}
            type="button"
            onClick={() => onSelect?.(b)}
            disabled={!onSelect}
            className={`card p-4 text-start transition-shadow ${onSelect ? 'hover:shadow-md' : ''} ${selectedId === b.leaveTypeId ? 'ring-2 ring-brand-500' : ''}`}
          >
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: b.color }} />
                {t(b.leaveTypeName)}
              </p>
              <p className="text-[11px] text-slate-500">
                {fmtDate(b.yearStart, 'dd LLL yy')} – {fmtDate(b.yearEnd, 'dd LLL yy')}
              </p>
            </div>
            <p className="mt-3 text-3xl font-semibold text-slate-900">
              {Math.round(b.remaining * 100) / 100}
              <span className="ms-1 text-sm font-normal text-slate-500">{t('days left')}</span>
            </p>
            <div className="mt-3 flex h-2 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full" style={{ width: `${usedPct}%`, backgroundColor: b.color }} />
              <div className="h-full opacity-40" style={{ width: `${pendPct}%`, backgroundColor: b.color }} />
            </div>
            <dl className="mt-3 grid grid-cols-4 gap-1 text-center text-xs">
              <div>
                <dt className="text-slate-500">{t('Entitled')}</dt>
                <dd className="font-medium text-slate-800">{b.entitled}</dd>
              </div>
              <div>
                <dt className="text-slate-500">{t('Used')}</dt>
                <dd className="font-medium text-slate-800">{b.used}</dd>
              </div>
              <div>
                <dt className="text-slate-500">{t('Pending')}</dt>
                <dd className="font-medium text-slate-800">{b.pending}</dd>
              </div>
              <div title={t('Carried over from last year and manual adjustments')}>
                <dt className="text-slate-500">{t('Extra')}</dt>
                <dd className="font-medium text-slate-800">{b.carriedOver + b.adjusted ? `${b.carriedOver + b.adjusted > 0 ? '+' : ''}${b.carriedOver + b.adjusted}` : '—'}</dd>
              </div>
            </dl>
            {b.accrualMethod === 'monthly' && b.accrued < b.entitled && (
              <p className="mt-2 text-[11px] text-slate-500">{t('Earned so far: {accrued} of {entitled} (monthly accrual)', { accrued: b.accrued, entitled: b.entitled })}</p>
            )}
            {b.rules && <RuleNotes b={b} />}
          </button>
        );
      })}
    </div>
    {limits.length > 0 && (
      <div className="card divide-y divide-slate-100">
        {limits.map((b) => (
          <div key={b.leaveTypeId} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="flex items-center gap-2 text-sm font-medium text-slate-800">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: b.color }} />
              {t(b.leaveTypeName)}
              {b.rules!.maxDaysPerYear ? (
                <span className="text-xs font-normal text-slate-500">
                  {t('{used} of {max} days used this year', { used: b.rules!.takenThisYear, max: b.rules!.maxDaysPerYear })}
                </span>
              ) : b.rules!.maxTimesPerCareer === 1 ? (
                <span className="text-xs font-normal text-slate-500">{b.rules!.timesUsed ? t('Already used') : t('Not used yet')}</span>
              ) : b.rules!.maxTimesPerCareer ? (
                <span className="text-xs font-normal text-slate-500">{t('used {n} of {max} times', { n: b.rules!.timesUsed, max: b.rules!.maxTimesPerCareer })}</span>
              ) : null}
            </p>
            <p className="text-xs text-slate-500">
              {ruleLines({ ...b.rules!, maxDaysPerYear: null, minServiceMonths: b.rules!.availableFrom ? b.rules!.minServiceMonths : null }, b.rules!.drawsFromTypeName).join(' · ')}
            </p>
          </div>
        ))}
      </div>
    )}
    </div>
  );
}

function RuleNotes({ b }: { b: LeaveBalanceDto }) {
  const r = b.rules!;
  // The service requirement only matters until it is met (then "Can be used from" disappears too).
  const lines = ruleLines({ ...r, maxDaysPerYear: null, requiresDocument: false, minServiceMonths: null });
  return (
    <>
      {r.availableFrom && <p className="mt-2 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800">{t('Can be used from {date}', { date: fmtDate(r.availableFrom, 'dd LLL yyyy') })}</p>}
      {lines.length > 0 && <p className="mt-2 text-[11px] text-slate-500">{lines.join(' · ')}</p>}
    </>
  );
}
